import * as React from 'react';
import type {V1ContextInput} from '../../schema/types';

export type V1Context = Readonly<{
  accountId: string | null;
  id: string | null;
  apiKey: string | null;
  betaHeaders: NonNullable<V1ContextInput['betaHeaders']> | null;
  // Returns null if in Legacy testmode
  compartmentId: string | null;
  liveMode: NonNullable<V1ContextInput['liveMode']> | null;
}>;

type V1ProviderProps = Partial<
  V1Context & {
    children: React.ReactNode;
  }
>;

const V1ContextContext = React.createContext<V1Context>({
  accountId: null,
  id: null,
  apiKey: null,
  betaHeaders: null,
  compartmentId: null,
  liveMode: null,
});

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export function useV1Context(): V1Context {
  return React.useContext(V1ContextContext);
}

export function V1Provider({
  accountId,
  id,
  apiKey,
  betaHeaders,
  children,
  compartmentId,
  liveMode,
}: V1ProviderProps) {
  const parentValue = React.useContext(V1ContextContext);

  const value: V1Context = React.useMemo(() => {
    const resolvedAccountIdValue =
      accountId === undefined ? parentValue.accountId : accountId;

    const resolvedIdValue = id === undefined ? parentValue.id : id;

    const resolvedApiKeyValue =
      apiKey === undefined ? parentValue.apiKey : apiKey;

    const resolvedBetaHeaders =
      betaHeaders === undefined ? parentValue.betaHeaders : betaHeaders;

    const resolvedCompartmentIdValue =
      compartmentId === undefined ? parentValue.compartmentId : compartmentId;

    const resolvedLiveModeValue =
      liveMode === undefined ? parentValue.liveMode : liveMode;

    return {
      accountId: resolvedAccountIdValue,
      id: resolvedIdValue,
      apiKey: resolvedApiKeyValue,
      betaHeaders: resolvedBetaHeaders,
      compartmentId: resolvedCompartmentIdValue,
      liveMode: resolvedLiveModeValue,
    };
  }, [
    parentValue,
    accountId,
    apiKey,
    betaHeaders,
    compartmentId,
    liveMode,
    id,
  ]);

  return (
    <V1ContextContext.Provider value={value}>
      {children}
    </V1ContextContext.Provider>
  );
}
